/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { create } from 'zustand';
import { AccessControlListValues, EntityFileExplorerItem } from '../../../../Services/file-store';
import { FileExplorerRawRowData, FileExplorerRowData } from './FileExplorer.defs';

export type FileExplorerStore = FileExplorerStates &
  FileExplorerStoreSetters &
  FileExplorerStoreUtilitySetters &
  FileExplorerStoreUtilityStates;

export interface FileExplorerStoreSetters {
  setStagedFilesToUpload: (files: FileList | Array<File>) => void;
  setDirectoryContents: (items: Array<EntityFileExplorerItem>) => void;
  setFileExplorerPosition: (path: string) => void;
  setStagedFileExplorerPosition: (path: string) => void;
  setSelectedRows: (rows: Array<FileExplorerRawRowData>) => void;
  setDirectoryViewerSelection: (path: string | null) => void;
}

export interface FileExplorerStoreUtilitySetters {
  setClearSelectedRows: (val: boolean) => void;
}

export interface FileExplorerStoreUtilityStates {
  clearSelectedRows: boolean;
}
export interface FileExplorerStates {
  directoryViewerSelection: string | null;
  stagedFilesToUpload: Array<File>;
  directoryContents: Array<EntityFileExplorerItem>;
  fileExplorerPosition: string;
  stagedFileExplorerPosition: string;
  selectedRows: Array<FileExplorerRowData>;
}

const initialState: FileExplorerStates & FileExplorerStoreUtilityStates = {
  clearSelectedRows: false,
  directoryContents: [],
  directoryViewerSelection: null,
  fileExplorerPosition: '/',
  selectedRows: [],
  stagedFileExplorerPosition: '/',
  stagedFilesToUpload: [],
};

export const useFileExplorerStore = create<FileExplorerStore>()((set) => ({
  ...initialState,
  setClearSelectedRows: (val): void => {
    set({
      clearSelectedRows: val,
    });
  },
  setDirectoryContents: (items): void => {
    set({
      directoryContents: items,
    });
  },
  setDirectoryViewerSelection: (data): void => {
    set({
      directoryViewerSelection: data,
    });
  },
  setFileExplorerPosition: (path): void => {
    set({
      fileExplorerPosition: path,
    });
  },
  setSelectedRows: (rows: Array<FileExplorerRawRowData>): void => {
    const mappedData = rows.map((row: FileExplorerRawRowData) => {
      const id = row.name.props.id || '';
      const path = row.name.props['data-path'] || '';
      const type = row.name.props['data-type'] || '';
      const visibility = row.name.props['data-visibility'] || AccessControlListValues.PRIVATE;

      return {
        id,
        path,
        type,
        visibility,
      };
    });
    set({
      selectedRows: mappedData,
    });
  },
  setStagedFileExplorerPosition: (path): void => {
    set({
      stagedFileExplorerPosition: path,
    });
  },
  setStagedFilesToUpload: (files): void => {
    // FileList is difficult to mock correctly in tests
    // istanbul ignore next
    if (files instanceof FileList) {
      return set({
        stagedFilesToUpload: Array.from(files),
      });
    }
    set({
      stagedFilesToUpload: files,
    });
  },
}));
